import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Alert,
    Grid
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDataProvider, useNotify } from 'react-admin';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import axios from 'axios';
import {
    VIRGIN_CATEGORY_FILTER,
    virginSubcategoriesByCategoryFilter,
} from '../utils/materialTypeFilters';
// @ts-ignore
import * as XLSX from 'xlsx';

interface DailyPricingProduct {
    id: number;
    product_category_id: number;
    product_sub_category_id: number | null;
    product_name: string;
    category_name: string;
    sub_category_name: string | null;
    product_grade: string;
    product_grade_raw: string | null;
    product_mfi_raw: string | null;
    city_name: string;
    customer_price: number;
}

interface State {
    id: number;
    state_name: string;
    state_code: string;
}

interface City {
    id: number;
    city_name: string;
}

interface Category {
    id: number;
    category_name: string;
}

interface SubCategory {
    id: number;
    sub_category_name: string;
}

const DailyPricing: React.FC = () => {
    useAuthorize([UserRole.Admin]);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [states, setStates] = useState<State[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    
    const [selectedState, setSelectedState] = useState<string>('');
    const [selectedCity, setSelectedCity] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('0');
    const [selectedSubCategory, setSelectedSubCategory] = useState<string>('0');
    
    const [products, setProducts] = useState<DailyPricingProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch states
    useEffect(() => {
        dataProvider.getList('states', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'state_name', order: 'ASC' },
            filter: { active: true }
        }).then(({ data }) => {
            setStates(data);
        }).catch(err => {
            console.error('Error fetching states:', err);
        });
    }, [dataProvider]);

    // Fetch cities when state is selected
    useEffect(() => {
        if (selectedState) {
            const state = states.find(s => s.state_code === selectedState);
            if (state) {
                dataProvider.getList('statecities', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'city_name', order: 'ASC' },
                    filter: { state_id: state.id }
                }).then(({ data }) => {
                    // Add Ex-Godown option
                    const citiesWithExGodown = [{ id: 0, city_name: 'Ex-Godown' }, ...data];
                    setCities(citiesWithExGodown);
                }).catch(err => {
                    console.error('Error fetching cities:', err);
                });
            }
        } else {
            setCities([]);
            setSelectedCity('');
        }
    }, [selectedState, states, dataProvider]);

    // Fetch categories
    useEffect(() => {
        dataProvider.getList('categories', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'category_name', order: 'ASC' },
            filter: VIRGIN_CATEGORY_FILTER,
        }).then(({ data }) => {
            setCategories(data);
        }).catch(err => {
            console.error('Error fetching categories:', err);
        });
    }, [dataProvider]);

    // Fetch subcategories when category is selected
    useEffect(() => {
        if (selectedCategory && selectedCategory !== '0') {
            dataProvider.getList('subcategories/bycategory', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'sub_category_name', order: 'ASC' },
                filter: virginSubcategoriesByCategoryFilter(selectedCategory),
            }).then(({ data }) => {
                setSubCategories(data);
            }).catch(err => {
                console.error('Error fetching subcategories:', err);
            });
        } else {
            setSubCategories([]);
            setSelectedSubCategory('0');
        }
    }, [selectedCategory, dataProvider]);

    // Fetch products when filters change
    useEffect(() => {
        if (selectedState && selectedCity !== '') {
            fetchProducts();
        } else {
            setProducts([]);
        }
    }, [selectedState, selectedCity, selectedCategory, selectedSubCategory]);

    const fetchProducts = async () => {
        if (!selectedState || selectedCity === '') return;

        setLoading(true);
        setError(null);

        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            // If base URL already includes /api, just add /dailypricing, otherwise add /api/dailypricing
            const endpoint = apiUrl.endsWith('/api') ? `${apiUrl}/dailypricing` : `${apiUrl}/api/dailypricing`;
            const response = await axios.get(endpoint, {
                params: {
                    state_code: selectedState,
                    city_id: selectedCity,
                    category_id: selectedCategory === '0' ? 0 : selectedCategory,
                    sub_category_id: selectedSubCategory === '0' ? 0 : selectedSubCategory,
                    interval: 2
                }
            });

            setProducts(response.data);
        } catch (err: any) {
            console.error('Error fetching products:', err);
            setError(err.response?.data?.error || 'Failed to fetch products');
            notify('Error fetching products', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (products.length === 0) {
            notify('No data to export', { type: 'warning' });
            return;
        }

        const wsData = [
            ['Product Name', 'Category Name', 'Sub-category Name', 'Product Grade', 'MFI', 'City Name', 'Price']
        ];

        products.forEach(product => {
            const price = typeof product.customer_price === 'number' 
                ? product.customer_price 
                : parseFloat(product.customer_price) || 0;
            wsData.push([
                product.product_name,
                product.category_name,
                product.sub_category_name || '',
                product.product_grade_raw || '',
                product.product_mfi_raw || '',
                product.city_name,
                price.toFixed(2)
            ]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'Daily Pricing');

        const fileName = `Daily_Pricing_${selectedState}_${selectedCity}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        notify('Export successful', { type: 'success' });
    };

    const handleCopyPrice = () => {
        if (products.length === 0) {
            notify('No data to copy', { type: 'warning' });
            return;
        }

        const state = states.find(s => s.state_code === selectedState);
        const city = cities.find(c => c.id.toString() === selectedCity);
        const stateCityName = city?.city_name || '';

        // Group products by category and subcategory
        const grouped = products.reduce((acc, product) => {
            const categoryKey = product.category_name;
            const subCategoryKey = product.sub_category_name || '';

            if (!acc[categoryKey]) {
                acc[categoryKey] = {};
            }
            if (!acc[categoryKey][subCategoryKey]) {
                acc[categoryKey][subCategoryKey] = [];
            }
            acc[categoryKey][subCategoryKey].push(product);
            return acc;
        }, {} as Record<string, Record<string, DailyPricingProduct[]>>);

        let formattedText = `📍 ${stateCityName}\n\n`;
        formattedText += '-------------------------\n\n';

        Object.keys(grouped).forEach(categoryName => {
            const categoryData = grouped[categoryName];
            
            Object.keys(categoryData).forEach(subCategoryName => {
                const productsInGroup = categoryData[subCategoryName];
                
                formattedText += `📦 ${categoryName}`;
                if (subCategoryName) {
                    formattedText += ` / ${subCategoryName}`;
                }
                formattedText += '\n\n';

                productsInGroup.forEach(product => {
                    const price = typeof product.customer_price === 'number' 
                        ? product.customer_price 
                        : parseFloat(product.customer_price) || 0;
                    formattedText += `Product: ${product.product_name}\n`;
                    if (product.product_grade_raw) {
                        formattedText += `Grade: ${product.product_grade_raw}\n`;
                    }
                    formattedText += `Price: ₹${price.toFixed(2)}\n\n`;
                });
            });
        });

        navigator.clipboard.writeText(formattedText).then(() => {
            notify('Prices copied to clipboard!', { type: 'success' });
        }).catch(err => {
            console.error('Failed to copy:', err);
            notify('Failed to copy to clipboard', { type: 'error' });
        });
    };

    const filteredProducts = useMemo(() => {
        let filtered = products;

        if (selectedCategory !== '0') {
            filtered = filtered.filter(p => p.product_category_id.toString() === selectedCategory);
        }

        if (selectedSubCategory !== '0') {
            filtered = filtered.filter(p => p.product_sub_category_id?.toString() === selectedSubCategory);
        }

        return filtered;
    }, [products, selectedCategory, selectedSubCategory]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Daily Pricing
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>State</InputLabel>
                            <Select
                                value={selectedState}
                                onChange={(e) => {
                                    setSelectedState(e.target.value);
                                    setSelectedCity('');
                                }}
                                label="State"
                            >
                                {states.map((state) => (
                                    <MenuItem key={state.id} value={state.state_code}>
                                        {state.state_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth disabled={!selectedState}>
                            <InputLabel>City</InputLabel>
                            <Select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                label="City"
                            >
                                {cities.map((city) => (
                                    <MenuItem key={city.id} value={city.id.toString()}>
                                        {city.city_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={selectedCategory}
                                onChange={(e) => {
                                    setSelectedCategory(e.target.value);
                                    setSelectedSubCategory('0');
                                }}
                                label="Category"
                            >
                                <MenuItem value="0">All Categories</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id.toString()}>
                                        {category.category_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth disabled={selectedCategory === '0'}>
                            <InputLabel>Sub-category</InputLabel>
                            <Select
                                value={selectedSubCategory}
                                onChange={(e) => setSelectedSubCategory(e.target.value)}
                                label="Sub-category"
                            >
                                <MenuItem value="0">All Sub-categories</MenuItem>
                                {subCategories.map((subCategory) => (
                                    <MenuItem key={subCategory.id} value={subCategory.id.toString()}>
                                        {subCategory.sub_category_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={filteredProducts.length === 0 || loading}
                    >
                        EXPORT
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyPrice}
                        disabled={filteredProducts.length === 0 || loading}
                    >
                        COPY PRICE
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Product Name</TableCell>
                                <TableCell>Category Name</TableCell>
                                <TableCell>Sub-category Name</TableCell>
                                <TableCell>Product Grade</TableCell>
                                <TableCell>MFI</TableCell>
                                <TableCell>City Name</TableCell>
                                <TableCell>Price</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredProducts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {selectedState && selectedCity !== '' 
                                            ? 'No products found' 
                                            : 'Please select State and City to view products'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredProducts.map((product) => (
                                    <TableRow key={product.id}>
                                        <TableCell>{product.product_name}</TableCell>
                                        <TableCell>{product.category_name}</TableCell>
                                        <TableCell>{product.sub_category_name || ''}</TableCell>
                                        <TableCell>{product.product_grade_raw || ''}</TableCell>
                                        <TableCell>{product.product_mfi_raw || ''}</TableCell>
                                        <TableCell>{product.city_name}</TableCell>
                                        <TableCell>
                                            ₹{(() => {
                                                const price = typeof product.customer_price === 'number' 
                                                    ? product.customer_price 
                                                    : parseFloat(product.customer_price) || 0;
                                                return price.toFixed(2);
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default DailyPricing;

